import { CompetitionFilterTypes } from "~pages/Competitions/CompetitionsListPage/enums/CompetitionFilterTypes.ts";
import { CompetitionTableColumns } from "~pages/Competitions/CompetitionsListPage/enums/CompetitionTableColumns.ts";
import { EntityTableColumns } from "~pages/Entities/EntitiesListPage/enums/EntityTableColumns.ts";
import { EntityTableFiltersTypes } from "~pages/Entities/EntitiesListPage/enums/EntityTableFiltersTypes.ts";
import { FixturesFilterTypes } from "~pages/Fixtures/FixtureListPage/types";
import { FixtureTableColumns } from "~pages/Fixtures/FixtureListPage/types/fixtureColumnsEnum.ts";
import { PersonTableColumns } from "~pages/Persons/PersonListPage/enums/PersonTableColumns.ts";
import { PersonTableFiltersTypes } from "~pages/Persons/PersonListPage/enums/PersonTableFiltersTypes.ts";
import { FixturePlayByPlayEventColumns } from "~pages/Reports/components/FixturePlayByPlayFlaggedEventDataTable/enums/FixturePlayByPlayEventColumns.ts";
import { FixturePlayByPlayEventFilters } from "~pages/Reports/components/FixturePlayByPlayFlaggedEventDataTable/enums/FixturePlayByPlayEventFilters.ts";
import { SeasonTableColumns } from "~pages/Seasons/SeasonListPage/enums/SeasonTableColumns.ts";
import { SeasonTableFilterTypes } from "~pages/Seasons/SeasonListPage/enums/SeasonTableFilterTypes.ts";
import { SiteTableColumns } from "~pages/Sites/SiteListPage/enums/SiteTableColumns.ts";
import { SiteTableFilters } from "~pages/Sites/SiteListPage/enums/SiteTableFilters.ts";
import { VenueTableColumns } from "~pages/Venues/VenueListPage/enums/VenueTableColumns.ts";
import { VenueTableFilterTypes } from "~pages/Venues/VenueListPage/enums/VenueTableFilterTypes.ts";
import type { DataTableColumnGroupDefinition } from "~types/dataTableColumnGroupDefinition.ts";
import type { TableColumnGroupDefinition } from "~types/tableColumnGroupDefinition.ts";
import type { UiConfiguration } from "~types/uiConfiguration.ts";

const fixtureTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "fixtures.table.column.chooser.title.details",
    columns: [
      { key: FixtureTableColumns.FixtureId },
      { key: FixtureTableColumns.ExternalId },
      { key: FixtureTableColumns.RoundNumber, defaultInColumnChooser: true },
      { key: FixtureTableColumns.HomeCompetitor, defaultInColumnChooser: true },
      { key: FixtureTableColumns.Result, defaultInColumnChooser: true },
      { key: FixtureTableColumns.AwayCompetitor, defaultInColumnChooser: true },
      { key: FixtureTableColumns.FixtureNumber, defaultInColumnChooser: true },
      { key: FixtureTableColumns.NameLocal },
      { key: FixtureTableColumns.NameLatin },
      { key: FixtureTableColumns.VenueId, defaultInColumnChooser: true },
      { key: FixtureTableColumns.StartDate, defaultInColumnChooser: true },
      { key: FixtureTableColumns.StartTime, defaultInColumnChooser: true },
      { key: FixtureTableColumns.FixtureType, defaultInColumnChooser: true },
      { key: FixtureTableColumns.Status, defaultInColumnChooser: true },
      { key: FixtureTableColumns.Stage, defaultInColumnChooser: true },
      { key: FixtureTableColumns.StageCode },
      { key: FixtureTableColumns.Pool, defaultInColumnChooser: true },
      { key: FixtureTableColumns.PoolCode },
      { key: FixtureTableColumns.Series },
      { key: FixtureTableColumns.SeriesCode },
      { key: FixtureTableColumns.Added },
      { key: FixtureTableColumns.Attendance },
      { key: FixtureTableColumns.Discipline },
      { key: FixtureTableColumns.StartTimeActualUtc },
      { key: FixtureTableColumns.EndTimeActualUtc },
      { key: FixtureTableColumns.FixtureProfile },
      { key: FixtureTableColumns.ProfileId },
      { key: FixtureTableColumns.InternationalReference },
      { key: FixtureTableColumns.Round },
      { key: FixtureTableColumns.RoundCode },
      { key: FixtureTableColumns.SeriesFixtureNumber },
      { key: FixtureTableColumns.TimesUnconfirmed },
      { key: FixtureTableColumns.StartTimeUtc },
    ],
  },
];

const competitionTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "competitions.table.column.chooser.title.details",
    columns: [
      { key: CompetitionTableColumns.CompetitionId },
      { key: CompetitionTableColumns.ExternalId },
      { key: CompetitionTableColumns.Images, defaultInColumnChooser: true },
      { key: CompetitionTableColumns.NameLocal, defaultInColumnChooser: true },
      { key: CompetitionTableColumns.NameLatin },
      { key: CompetitionTableColumns.AbbreviationLocal, defaultInColumnChooser: true },
      { key: CompetitionTableColumns.AbbreviationLatin },
      { key: CompetitionTableColumns.AgeGroup, defaultInColumnChooser: true },
      { key: CompetitionTableColumns.InternationalReference },
      { key: CompetitionTableColumns.League },
    ],
  },
];

const seasonTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "seasons.table.column.chooser.title.details",
    columns: [
      { key: SeasonTableColumns.Image },
      { key: SeasonTableColumns.Year, defaultInColumnChooser: true },
      { key: SeasonTableColumns.CompetitionId },
      { key: SeasonTableColumns.CompetitionName, defaultInColumnChooser: true },
      { key: SeasonTableColumns.SeasonId },
      { key: SeasonTableColumns.ExternalId },
      { key: SeasonTableColumns.NameLocal, defaultInColumnChooser: true },
      { key: SeasonTableColumns.Gender, defaultInColumnChooser: true },
      { key: SeasonTableColumns.AgeGroup, defaultInColumnChooser: true },
      { key: SeasonTableColumns.Status, defaultInColumnChooser: true },
      { key: SeasonTableColumns.SeasonType },
      { key: SeasonTableColumns.Representation },
      { key: SeasonTableColumns.Standard },
      { key: SeasonTableColumns.Discipline },
      { key: SeasonTableColumns.StartDate },
      { key: SeasonTableColumns.EndDate },
    ],
  },
];
const venuesTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "venues.table.column.chooser.title.details",
    columns: [
      { key: VenueTableColumns.NameLocal, defaultInColumnChooser: true },
      { key: VenueTableColumns.NameLatin },
      { key: VenueTableColumns.AbbreviationLocal },
      { key: VenueTableColumns.AbbreviationLatin },
      { key: VenueTableColumns.VenueId, defaultInColumnChooser: true },
      { key: VenueTableColumns.ExternalId, defaultInColumnChooser: true },
      { key: VenueTableColumns.CountryCode, defaultInColumnChooser: true },
      { key: VenueTableColumns.TimeZone, defaultInColumnChooser: true },
      { key: VenueTableColumns.Capacity, defaultInColumnChooser: true },
      { key: VenueTableColumns.Status, defaultInColumnChooser: true },
    ],
  },
];
const sitesTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "sites.table.column.chooser.title.details",
    columns: [
      { key: SiteTableColumns.NameLocal, defaultInColumnChooser: true },
      { key: SiteTableColumns.NameLatin },
      { key: SiteTableColumns.AbbreviationLocal },
      { key: SiteTableColumns.AbbreviationLatin },
      { key: SiteTableColumns.SiteId, defaultInColumnChooser: true },
      { key: SiteTableColumns.ExternalId, defaultInColumnChooser: true },
      { key: SiteTableColumns.CountryCode, defaultInColumnChooser: true },
    ],
  },
];
const personsTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "sites.table.column.chooser.title.details",
    columns: [
      { key: PersonTableColumns.Image },
      { key: PersonTableColumns.NameFullLocal, defaultInColumnChooser: true },
      { key: PersonTableColumns.NameFullLatin },
      { key: PersonTableColumns.NameFamilyLocal },
      { key: PersonTableColumns.NameFamilyLatin },
      { key: PersonTableColumns.NameGivenLocal },
      { key: PersonTableColumns.NameGivenLatin },
      { key: PersonTableColumns.NameAbbreviated },
      { key: PersonTableColumns.KnownAs },
      { key: PersonTableColumns.PersonId, defaultInColumnChooser: true },
      { key: PersonTableColumns.ExternalId, defaultInColumnChooser: true },
      { key: PersonTableColumns.Gender, defaultInColumnChooser: true },
      { key: PersonTableColumns.Nationality, defaultInColumnChooser: true },
      { key: PersonTableColumns.LanguageLocal },
      { key: PersonTableColumns.Representation },
      { key: PersonTableColumns.Dob, defaultInColumnChooser: true },
      { key: PersonTableColumns.Status, defaultInColumnChooser: true },
      { key: PersonTableColumns.Deceased },
      { key: PersonTableColumns.Height },
      { key: PersonTableColumns.Weight },
      { key: PersonTableColumns.HeightImperial },
      { key: PersonTableColumns.WeightImperial },
      { key: PersonTableColumns.JuniorAssociationLeague },
      { key: PersonTableColumns.School },
      { key: PersonTableColumns.SchoolClass },
      { key: PersonTableColumns.HomeTown },
      { key: PersonTableColumns.College },
      { key: PersonTableColumns.CollegeClass },
    ],
  },
];
const entitiesTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "entities.table.column.chooser.title.details",
    columns: [
      { key: EntityTableColumns.Image },
      { key: EntityTableColumns.NameFullLocal, defaultInColumnChooser: true },
      { key: EntityTableColumns.EntityId, defaultInColumnChooser: true },
      { key: EntityTableColumns.ExternalId, defaultInColumnChooser: true },
      { key: EntityTableColumns.NameFullLatin },
      { key: EntityTableColumns.AgeGroup, defaultInColumnChooser: true },
      { key: EntityTableColumns.Gender, defaultInColumnChooser: true },
      { key: EntityTableColumns.Discipline, defaultInColumnChooser: true },
      { key: EntityTableColumns.Standard, defaultInColumnChooser: true },
      { key: EntityTableColumns.Status, defaultInColumnChooser: true },
      { key: EntityTableColumns.EntityGroupId },
      { key: EntityTableColumns.Representing },
      { key: EntityTableColumns.InternationalReference },
      { key: EntityTableColumns.Grade },
      { key: EntityTableColumns.CodeLocal },
      { key: EntityTableColumns.CodeLatin },
    ],
  },
];

const seasonRosterColumnGroups: Array<TableColumnGroupDefinition> = [
  {
    titleIntlKey: "roster.columns.person.title",
    columns: [
      { key: "personId", isDefault: true },
      { key: "nameFullLocal", isDefault: true },
      { key: "person.externalId", isDefault: true },
      { key: "person.nameFullLatin" },
      { key: "person.additionalDetails.height" },
      { key: "person.additionalDetails.weight" },
      { key: "person.additionalDetails.heightImperial" },
      { key: "person.additionalDetails.weightImperial" },
      { key: "person.additionalDetails.representation" },
      { key: "person.additionalDetails.juniorAssociationLeague" },
      { key: "person.additionalDetails.homeTown" },
      { key: "person.additionalDetails.college" },
      { key: "person.additionalDetails.collegeClass" },
      { key: "person.additionalDetails.school" },
      { key: "person.additionalDetails.schoolClass" },
      { key: "dob" },
      { key: "person.gender" },
      { key: "person.nationality" },
      { key: "person.representing" },
      { key: "person.status" },
    ],
  },
  {
    titleIntlKey: "roster.columns.roster.title",
    columns: [
      { key: "bib", isDefault: true },
      { key: "externalId", isDefault: true },
      { key: "position", isDefault: true },
      { key: "status", isDefault: true },
      { key: "isCaptain", isDefault: true },
    ],
  },
];

const fixtureRosterColumnGroups: Array<TableColumnGroupDefinition> = [
  {
    titleIntlKey: "roster.columns.person.title",
    columns: [
      { key: "personId", isDefault: true },
      { key: "nameFullLocal", isDefault: true },
      { key: "person.externalId", isDefault: true },
      { key: "person.nameFullLatin" },
      { key: "person.additionalDetails.height" },
      { key: "person.additionalDetails.weight" },
      { key: "person.additionalDetails.heightImperial" },
      { key: "person.additionalDetails.weightImperial" },
      { key: "person.additionalDetails.representation" },
      { key: "person.additionalDetails.juniorAssociationLeague" },
      { key: "person.additionalDetails.homeTown" },
      { key: "person.additionalDetails.college" },
      { key: "person.additionalDetails.collegeClass" },
      { key: "person.additionalDetails.school" },
      { key: "person.additionalDetails.schoolClass" },
      { key: "dob" },
      { key: "person.gender" },
      { key: "person.nationality" },
      { key: "person.representing" },
      { key: "person.status" },
    ],
  },
  {
    titleIntlKey: "roster.columns.roster.title",
    columns: [
      { key: "bib", isDefault: true },
      { key: "externalId", isDefault: true },
      { key: "position", isDefault: true },
      { key: "starter", isDefault: true },
      { key: "status", isDefault: true },
      { key: "isCaptain", isDefault: true },
    ],
  },
];
const fixturePlayByPlayFlaggedEventTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "fixturePBPEvent.table.column.chooser.title.details",
    columns: [
      { key: FixturePlayByPlayEventColumns.Fixture, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.FixtureStartTime, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.FixtureId, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.FixtureStatus, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.EventId, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.PeriodId, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.EventTime, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.Entity, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.EntityId },
      { key: FixturePlayByPlayEventColumns.EventType, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.SubType, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.Status, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.Class },
      { key: FixturePlayByPlayEventColumns.ClientId },
      { key: FixturePlayByPlayEventColumns.Clock },
      { key: FixturePlayByPlayEventColumns.Flagged },
      { key: FixturePlayByPlayEventColumns.Highlight },
      { key: FixturePlayByPlayEventColumns.OfficialId },
      { key: FixturePlayByPlayEventColumns.Person },
      { key: FixturePlayByPlayEventColumns.PersonId },
      { key: FixturePlayByPlayEventColumns.PlayId },
      { key: FixturePlayByPlayEventColumns.Received },
      { key: FixturePlayByPlayEventColumns.Section },
      { key: FixturePlayByPlayEventColumns.Sequence },
      { key: FixturePlayByPlayEventColumns.ShotClock },
      { key: FixturePlayByPlayEventColumns.Success },
      { key: FixturePlayByPlayEventColumns.Date },
      { key: FixturePlayByPlayEventColumns.Topic },
      { key: FixturePlayByPlayEventColumns.Zone },
    ],
  },
];

//return back key config for columns, remove show from filterconfig
export const defaultUiConfig: UiConfiguration = {
  pages: {
    seasonRoster: { tableColumnGroups: seasonRosterColumnGroups },
    fixtureRoster: { tableColumnGroups: fixtureRosterColumnGroups },
    fixtures: {
      listPage: {
        filtersConfig: [
          { filterKey: FixturesFilterTypes.Stages },
          { filterKey: FixturesFilterTypes.RoundNumber },
          { filterKey: FixturesFilterTypes.Pools },
          { filterKey: FixturesFilterTypes.Competitors },
          { filterKey: FixturesFilterTypes.Venues },
          { filterKey: FixturesFilterTypes.FixtureStatuses },
          { filterKey: FixturesFilterTypes.DateRange },
        ],
        columnsConfig: fixtureTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.fixtures",
          descriptionIntlKey: "import.modal.text.fixtures",
        },
      },
    },
    competitions: {
      listPage: {
        columnsConfig: competitionTableColumnsArray,
        filtersConfig: [{ filterKey: CompetitionFilterTypes.AgeGroup }, { filterKey: CompetitionFilterTypes.League }],
      },
    },
    seasons: {
      listPage: {
        columnsConfig: seasonTableColumnsArray,
        filtersConfig: [
          {
            filterKey: SeasonTableFilterTypes.Gender,
          },
          {
            filterKey: SeasonTableFilterTypes.AgeGroup,
          },
          {
            filterKey: SeasonTableFilterTypes.Status,
          },
          {
            filterKey: SeasonTableFilterTypes.Standard,
          },
        ],
        importConfig: {
          titleIntlKey: "import.modal.title.seasons",
          descriptionIntlKey: "import.modal.text.seasons",
        },
      },
    },
    persons: {
      listPage: {
        filtersConfig: [
          {
            filterKey: PersonTableFiltersTypes.Nationality,
          },
          {
            filterKey: PersonTableFiltersTypes.Gender,
          },
          {
            filterKey: PersonTableFiltersTypes.Status,
          },
        ],
        columnsConfig: personsTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.persons",
          descriptionIntlKey: "import.modal.text.persons",
        },
      },
    },
    entities: {
      listPage: {
        filtersConfig: [
          {
            filterKey: EntityTableFiltersTypes.Gender,
          },
          {
            filterKey: EntityTableFiltersTypes.AgeGroup,
          },
          {
            filterKey: EntityTableFiltersTypes.Discipline,
          },
          {
            filterKey: EntityTableFiltersTypes.Standard,
          },
          {
            filterKey: EntityTableFiltersTypes.Status,
          },
        ],
        columnsConfig: entitiesTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.entities",
          descriptionIntlKey: "import.modal.text.entities",
        },
      },
    },
    reports: {
      fixturePlayByPlayFlaggedEventList: {
        columnsConfig: fixturePlayByPlayFlaggedEventTableColumnsArray,
        filtersConfig: [
          {
            filterKey: FixturePlayByPlayEventFilters.FixtureStatus,
          },
          {
            filterKey: FixturePlayByPlayEventFilters.DateRange,
          },
        ],
      },
    },
    dashboard: {
      listPage: {
        filtersConfig: [
          {
            filterKey: SeasonTableFilterTypes.CompetitionName,
          },
          {
            filterKey: SeasonTableFilterTypes.Gender,
          },
          {
            filterKey: SeasonTableFilterTypes.AgeGroup,
          },
          {
            filterKey: SeasonTableFilterTypes.Status,
          },
        ],
        columnsConfig: seasonTableColumnsArray,
      },
    },
    seasonUpsert: {
      promotionRelegationRules: {
        enabled: false,
      },
      disciplines: {
        enabled: false,
        isNullable: true,
      },
    },
    entityStatistic: {
      enabled: false,
    },
    venues: {
      listPage: {
        columnsConfig: venuesTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.venues",
          descriptionIntlKey: "import.modal.text.venues",
        },
        filtersConfig: [
          {
            filterKey: VenueTableFilterTypes.CountryCode,
          },
          {
            filterKey: VenueTableFilterTypes.Status,
          },
        ],
      },
    },
    sites: {
      listPage: {
        columnsConfig: sitesTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.sites",
          descriptionIntlKey: "import.modal.text.sites",
        },
        filtersConfig: [
          {
            filterKey: SiteTableFilters.CountryCode,
          },
        ],
      },
    },
  },
};
